import React, { FC } from 'react'
import { Box } from '@material-ui/core'
import { useNavigate } from 'react-router-dom'
import { ADMIN_PORTAL_ROLES, ROLES, Title } from 'prace-common-components'
import { useAppSelector } from 'store/hooks'
import { useGetImpersonationActionsQuery } from 'store/api/users'
import { ImpersonationAction } from 'store/api/users/types'
import { Routes } from 'constants/global'
import { adminName, describeAction, getActionChanges } from './format'
import { $Action, $ActionMeta, $ActionTitle, $AppLink, $ChangeItem, $ChangeList, $Empty } from './styles'

/* Mirrors the Authorize scope on GET /users/impersonation/actions (users/routes.ts). Every caller of
   that endpoint must gate on this — SUPER_ADMIN also logs in here and would otherwise be logged out.
   PRO sees the timelines but can neither impersonate nor edit a submitted value. */
export const CAN_VIEW_IMPERSONATION = ADMIN_PORTAL_ROLES

type ItemProps = {
  action: ImpersonationAction,
  /* Resolve a form element id to its title (only where the call's form definition is available). */
  resolveTitle?: (elementId: number) => string | undefined,
  /* 'link' = clickable app uid (History tab), 'hidden' = omit the suffix (already on that application). */
  application?: 'link' | 'hidden',
}

/* One readable audit entry. Shared by the application Timeline and the user History tab. */
export const ImpersonationActionItem: FC<ItemProps> = ({ action, resolveTitle, application = 'link' }) => {
  const navigate = useNavigate()
  const { label, ofApplication } = describeAction(action)
  const uid = action.applicationUid
  const showSuffix = ofApplication && !!uid && application !== 'hidden'
  /* Application-scoped details live in that application's timeline — here we just link to it.
     Non-application actions (profile, email, …) show their changes inline, since there's nowhere else. */
  const changes = ofApplication ? [] : getActionChanges(action, resolveTitle)

  return (
    <$Action>
      <$ActionTitle>
        {label}
        {showSuffix ? <>
          {' of application '}
          <$AppLink onClick={() => navigate(Routes.APPLICATION(uid as string))}>{uid}</$AppLink>
        </> : null}
      </$ActionTitle>
      <$ActionMeta>
        by {adminName(action)} (Admin) · {new Date(action.createdAt).toLocaleString()}
        {action.statusCode !== 200 ? ` · status ${action.statusCode}` : ''}
      </$ActionMeta>
      {!!changes.length && (
        <$ChangeList>
          {changes.map((change, idx) => (
            <$ChangeItem key={idx}>
              <b>{change.label}</b>{' → '}{change.value || <$Empty>(empty)</$Empty>}
            </$ChangeItem>
          ))}
        </$ChangeList>
      )}
    </$Action>
  )
}

type Props = {
  /* Pass one: application timeline, or a user's full impersonation history. */
  applicationId?: number,
  targetId?: number,
  hideWhenEmpty?: boolean,
}

/* Read-only audit panel: everything an impersonating admin did for one user (History tab). */
export const ImpersonationActivity: FC<Props> = ({ applicationId, targetId, hideWhenEmpty }) => {
  const role = useAppSelector((state) => state.auth.user?.role)
  /* The endpoint is admin-scoped; a PRO firing it gets a 403, which the admin portal turns into a logout. */
  const skip = (!applicationId && !targetId) || !CAN_VIEW_IMPERSONATION.includes(role as ROLES)
  const { data: actions = [], isFetching } = useGetImpersonationActionsQuery(
    { applicationId, targetId }, { skip, refetchOnMountOrArgChange: true })

  if (skip) return null
  if (hideWhenEmpty && !isFetching && !actions.length) return null

  return (
    <Box>
      <Title alternate fontSize={18}>Impersonation activity</Title>
      {!isFetching && !actions.length && <$Empty>No impersonation actions recorded.</$Empty>}
      {actions.map((action) => (
        <ImpersonationActionItem key={action.id} action={action} application='link' />
      ))}
    </Box>
  )
}
